import { ageTone, ageWords, notACarrierName, pacificStamp, type TodoItem } from '@claims/core';
import { waited } from './components.js';
import { lettersOf, sfHrefs, sfKeyWords, SIDE_KEY_WORDS, sideTag, type KeyEntry } from './list/row-key.js';

/** A QUEUE ROW'S KEY (the circle i, ported 2026-10-18 from the medrec console; DESIGN_SYSTEM 8): only the marks the row carries, each drawn
 *  as it appears on the row, in the order they sit on it: the wait, the chips (why it is first, a second client, a record marked DO NOT
 *  USE), the side, the record (the policy number, or what is missing), and M and I. The words the Queue alone draws are said here; the
 *  shared ones (the sides, M and I) come from `list/row-key.ts`. Pure, no React. */
export const QUEUE_KEY_WORDS = Object.freeze({
  since: (start: string): string => `How long it has waited on the team (since ${start}). It turns red after 3 days.`,
  caseAge: (start: string): string => `How long the case has been open (since ${start}).`,
  urgent: (reasons: readonly string[]): string => `Why it is first in its group: ${reasons.join(', ')}.`,
  second: 'A second client from the same crash: it carries the main client\u{2019}s claim number.',
  doNotUse: (name: string): string => `The record\u{2019}s name says \u{201C}${name}\u{201D}. Check it in Salesforce.`,
  policy: 'The policy number on the record.',
  noPolicy: 'The record has no policy number yet.',
  noCarrier: 'No insurer is on the record yet, or it names one that is not an insurer.',
  noRecord: (side: string): string => `The case has no ${side} record; the row says what it takes.`,
});

/** The row's own marks after its first cell (the chips, the side, the record, the links): the Queue and All unopened draw the same. */
function rowMarks(it: TodoItem): KeyEntry[] {
  const r = it.row, m = it.matter, W = QUEUE_KEY_WORDS;
  const out: KeyEntry[] = [];
  if (it.urgent) out.push({ id: 'urgent', mark: { kind: 'tag', words: it.urgent.label }, words: W.urgent(it.urgent.reasons) });
  if (!m.lead && m.companionId) out.push({ id: 'second', mark: { kind: 'tag', words: '2nd' }, words: W.second });
  if (r.doNotUse) out.push({ id: 'dnu', mark: { kind: 'tag', words: 'DO NOT USE' }, words: W.doNotUse(r.doNotUse) });
  out.push({ id: 'side', mark: { kind: 'tag', words: sideTag(r.side) }, words: SIDE_KEY_WORDS[r.side] ?? `${r.side}.` });
  const realCarrier = r.carrier && !notACarrierName(r.carrier) ? r.carrier : null;
  out.push(r.gap ? { id: 'record', mark: { kind: 'text', text: `no ${r.side} record` }, words: W.noRecord(r.side) }
    : !realCarrier ? { id: 'record', mark: { kind: 'text', text: 'no carrier yet' }, words: W.noCarrier }
      : r.policy ? { id: 'record', mark: { kind: 'text', text: r.policy, tone: 'id' }, words: W.policy }
        : { id: 'record', mark: { kind: 'text', text: 'no policy #' }, words: W.noPolicy });
  const letters = lettersOf(sfHrefs(m.id, r.insuranceId));
  out.push({ id: 'links', mark: { kind: 'links', letters }, words: sfKeyWords(letters) });
  return out;
}

/** A Queue row (Today, 14 days): the wait first, red over 3 days, then the row's marks. */
export function queueKeyEntries(it: TodoItem, now: Date): KeyEntry[] {
  const r = it.row;
  const hot = ageTone(r.waitingSince, now) === 'old';
  return [
    { id: 'since', mark: hot ? { kind: 'text', text: waited(r.waitingSince, now.getTime()), tone: 'red' } : { kind: 'text', text: waited(r.waitingSince, now.getTime()) },
      words: QUEUE_KEY_WORDS.since(pacificStamp(r.waitingSince)) },
    ...rowMarks(it),
  ];
}

/** An All unopened row: the case's age first (never red: every row there is unopened, so red would be every row), then the row's marks. */
export function unopenedKeyEntries(it: TodoItem, age: { days: number; openedAt: string }): KeyEntry[] {
  return [{ id: 'since', mark: { kind: 'text', text: ageWords(age.days) }, words: QUEUE_KEY_WORDS.caseAge(pacificStamp(age.openedAt)) }, ...rowMarks(it)];
}
